import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { ScrollArea } from '../ui/scroll-area';
import { 
  HelpCircle, 
  MousePointer2,
  Clock,
  Trophy,
  Star,
  Puzzle,
  RotateCw,
  Check,
  Calendar,
  Flame,
  Swords,
  Share2,
  Users,
  ArrowLeft,
  Maximize,
  Image,
  Smartphone
} from 'lucide-react';

export function HelpModal({ isOpen, onClose }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] sm:w-auto max-h-[90vh] sm:max-h-[85vh] overflow-y-auto box-border" data-testid="help-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            {/* Back button for mobile */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="h-8 w-8 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <HelpCircle className="h-5 w-5" />
            Help
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Puzzle className="h-4 w-4" />
                How to Play
              </h3>
              <p className="text-sm text-muted-foreground">
                Drag puzzle pieces from the tray and drop them onto the grid. 
                Complete the puzzle by placing all pieces in their correct positions.
              </p>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <MousePointer2 className="h-4 w-4" />
                Controls
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• <strong>Drag & Drop:</strong> Click and drag pieces to the board</li>
                <li>• <strong>Swap Pieces:</strong> Drag pieces on the board to swap positions</li>
                <li>• <strong>Hover:</strong> Hover over the center of tray pieces to see them enlarged</li>
                <li>• <strong>Reference:</strong> Click the thumbnail to see the full image</li>
                <li>• <strong>Reset:</strong> Returns incorrectly placed pieces to the tray and shuffles them. Correctly placed pieces stay in place. Does not affect your score, moves, or timer.</li>
                <li>• <strong>Mobile:</strong> Touch and drag works on mobile devices</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Image className="h-4 w-4" />
                Piece Overlay
              </h3>
              <p className="text-sm text-muted-foreground">
                Compare pieces directly against the reference image to find their correct position:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Drag any piece over the reference image thumbnail (top-right)</li>
                <li>• The reference image goes fullscreen</li>
                <li>• Move the piece around to compare with the full image</li>
                <li>• Release the piece to return to normal view</li>
                <li>• <strong>Note:</strong> Each use adds 1 move penalty</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Maximize className="h-4 w-4" />
                Fullscreen Mode
              </h3>
              <p className="text-sm text-muted-foreground">
                On mobile devices, fullscreen mode activates automatically when you start playing 
                to give you the best puzzle experience. Use the fullscreen button in the toolbar 
                to toggle fullscreen on/off at any time.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>iPhone (installed PWA):</strong> iOS doesn't expose a fullscreen toggle to web apps, 
                so the installed app always runs full-screen. To exit, tap the <strong>X</strong> button in 
                the top-left corner (closes the app), or swipe up from the bottom edge of the screen 
                to return to your home screen.
              </p>
            </section>
            
            <Separator />
            
            <section data-testid="android-display-tips-section">
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Smartphone className="h-4 w-4" />
                Android Display Tips
              </h3>
              <p className="text-sm text-muted-foreground">
                If a screen or modal appears cut off on your Android device, your system display 
                settings may be scaled up. To fix this, open your phone's <strong>Settings → Display</strong> and try:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Lower the <strong>Screen Zoom</strong> (or "Display size") to the smallest setting</li>
                <li>• Lower the <strong>Font Size</strong> to default or smaller</li>
                <li>• Turn off <strong>Bold Font</strong> style</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-2">
                After adjusting, return to the game — everything should fit perfectly on your screen.
              </p>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4" />
                Daily Challenge
              </h3>
              <p className="text-sm text-muted-foreground">
                A new puzzle is available every day at midnight Eastern Time. Everyone plays the same 
                puzzle, so you can compete with players worldwide on the daily leaderboard!
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Fixed Normal difficulty for fair competition</li>
                <li>• One attempt per device per day</li>
                <li>• Build streaks by playing consecutive days</li>
                <li>• Fresh puzzle images rotate weekly via Unsplash</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Flame className="h-4 w-4" />
                Streaks & Badges
              </h3>
              <p className="text-sm text-muted-foreground">
                Play the daily challenge on consecutive days to build your streak and earn badges:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• <strong>🔥 On Fire:</strong> 3-day streak</li>
                <li>• <strong>⚡ Week Warrior:</strong> 7-day streak</li>
                <li>• <strong>🌟 Streak Champion:</strong> 14-day streak</li>
                <li>• <strong>💎 Streak Master:</strong> 30-day streak</li>
                <li>• <strong>👑 Streak Legend:</strong> 60-day streak</li>
                <li>• <strong>🏆 Streak God:</strong> 100-day streak</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Swords className="h-4 w-4" />
                Challenge Friends
              </h3>
              <p className="text-sm text-muted-foreground">
                Create custom challenges and compete with friends!
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Complete a puzzle, then click "Challenge Friends"</li>
                <li>• Enter your name to save your score first</li>
                <li>• Get a unique 6-character challenge code</li>
                <li>• Share via Facebook or use "More sharing options" on mobile</li>
                <li>• Friends compete on the same puzzle and difficulty</li>
                <li>• View win/loss history in the Challenges modal</li>
                <li>• Challenge links never expire</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4" />
                Puzzle History
              </h3>
              <p className="text-sm text-muted-foreground">
                Track your completed puzzles and replay favorites!
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Click the clock icon to view your history</li>
                <li>• See completion stats: puzzles solved, perfect games, average score</li>
                <li>• Toggle between gallery and list views</li>
                <li>• Click any completed puzzle to play it again</li>
                <li>• Progress bar shows completion toward all available images</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Share2 className="h-4 w-4" />
                Sharing & Score Cards
              </h3>
              <p className="text-sm text-muted-foreground">
                Share your achievements with dynamic score card images:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• <strong>Facebook:</strong> Posts with rich score card preview</li>
                <li>• <strong>More Options (Mobile):</strong> Downloads score card and opens native share sheet</li>
                <li>• Score cards show: puzzle thumbnail, score, time, moves, difficulty</li>
                <li>• Challenge cards include your rank and "Can you beat me?" text</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Trophy className="h-4 w-4" />
                Scoring
              </h3>
              <ul className="text-sm text-muted-foreground space-y-2">
                <li>• <strong>Time Score:</strong> 500 × e^(-seconds/120)</li>
                <li>• <strong>Move Score:</strong> 300 × (16 / moves)</li>
                <li>• <strong>Completion:</strong> +200 points</li>
                <li>• <strong>Multiplier:</strong> Easy ×0.75, Normal ×1.0, Hard ×1.5</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Star className="h-4 w-4" />
                Perfect Game
              </h3>
              <p className="text-sm text-muted-foreground">
                Complete the puzzle in exactly 16 moves to earn bonus points:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• Easy: +25 points</li>
                <li>• Normal: +50 points</li>
                <li>• Hard: +100 points</li>
              </ul>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <RotateCw className="h-4 w-4" />
                Hard Mode
              </h3>
              <p className="text-sm text-muted-foreground">
                In hard mode, pieces are randomly rotated. When you place a piece 
                in its correct position, it will automatically rotate to the correct orientation.
              </p>
            </section>
            
            <Separator />
            
            <section>
              <h3 className="font-heading font-semibold flex items-center gap-2 mb-2">
                <Users className="h-4 w-4" />
                Leaderboards
              </h3>
              <p className="text-sm text-muted-foreground">
                Multiple leaderboards track your achievements:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• <strong>Main Leaderboard:</strong> All-time high scores across all puzzles</li>
                <li>• <strong>Daily Leaderboard:</strong> Today's best scores with streaks shown</li>
                <li>• <strong>Challenge Leaderboards:</strong> Per-challenge rankings with HOST badge</li>
                <li>• <strong>Challenge History:</strong> Your win/loss record against friends</li>
              </ul>
            </section>
          </div>
        </ScrollArea>
        
        <div className="pt-4 border-t">
          <Button onClick={onClose} className="w-full">
            <Check className="h-4 w-4 mr-2" />
            Got it!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
